// Flat English/Bengali string dictionaries for the webapp shell.
//
// Ported from mobileapp/src/i18n/translations.ts — same two languages, same
// key style, and the same Bengali wording wherever a string exists in both
// apps, so someone who picks Bengali on their phone sees the same words here.
// The mobile file is the larger of the two (it covers every screen); this one
// covers the shell — sidebar, top bar, breadcrumb and the preferences screen.
// Individual screens get translated by replacing their hardcoded strings with
// t() calls and adding the keys below.
//
// `nav.*` keys are looked up BY MENU/ROUTE ID (see layout/Menu.tsx and
// layout/Layout.tsx), so translating a new page means adding `nav.<route id>`
// here and nothing else. A missing key falls back to the English label already
// in module-config, so a half-translated menu reads as English rather than as
// raw keys.
import { PLATFORM } from '../config/platform';

export const translations = {
  english: {
    // --- Navigation: keyed by menu item / route id --------------------------
    'nav.home': 'Dashboard',
    'nav.employee-portal': 'Dashboard',
    'nav.broker-dashboard': 'Dashboard',
    'nav.revenue': 'Revenue',
    'nav.organizations': 'Organizations',
    'nav.organization-subscriptions': 'Organization Subscriptions',
    'nav.organization-setup': 'Organization Setup',
    'nav.my-organizations': 'My Organizations',
    'nav.owners': 'Owners',
    'nav.owner': 'Owner',
    'nav.brokers': 'Brokers',
    'nav.broker': 'Broker',
    'nav.broker-referrals': 'Referrals',
    'nav.broker-profile': 'Profile',
    'nav.enterprise': 'Enterprise',
    'nav.plans': 'Plans',
    'nav.plan': 'Plan',
    'nav.subscriptions': 'Subscription',
    'nav.settings': 'Settings',
    'nav.preferences': 'Preferences',
    'nav.profile': 'Profile',
    'nav.users': 'Users',
    'nav.managers': 'Managers',
    'nav.employees': 'Employees',
    'nav.roles-permissions': 'Roles & Permissions',
    'nav.data-review': 'Data Review',
    'nav.rate-limit': 'Rate Limiting',
    'nav.api-usage': 'API Usage',
    'nav.app-config': 'App Configuration',
    'nav.email-config': 'Email Configuration',
    'nav.email-templates': 'Email Templates',
    'nav.gallery': 'Gallery',
    'nav.trash': 'Trash',
    'nav.about': 'About Us',
    'nav.contact': 'Contact Us',
    'nav.privacy': 'Privacy Policy',
    'nav.terms': 'Terms of Service',

    // Inventory — this product's own screens (see config/domain-config.tsx).
    'nav.inventory': 'Inventory',
    'nav.products': 'Products',
    'nav.warehouses': 'Warehouses',
    'nav.stock-levels': 'Stock Levels',
    'nav.stock-adjustments': 'Stock Adjustments',
    'nav.categories': 'Categories',
    'nav.customers': 'Customers',
    'nav.factories': 'Factories',
    'nav.brands': 'Brands',
    'nav.suppliers': 'Suppliers',
    'nav.orders': 'Purchase Orders',
    'nav.sales': 'Sales',
    'nav.invoices': 'Invoices',
    'nav.payments': 'Payments',

    // --- Modules (the workspace switcher in the top bar) --------------------
    'module.Platform Owner': 'Platform Owner',
    'module.Owner': 'Owner',
    'module.Admin': 'Admin',
    'module.Manager': 'Manager',
    'module.Employee': 'Employee',
    'module.Broker': 'Broker',

    // --- Top bar -----------------------------------------------------------
    'appbar.toggleSidebar': 'Toggle sidebar',
    'appbar.userMenu': 'User menu',
    'appbar.language': 'Language',
    'appbar.pageNotFound': 'Page not found',

    // --- Preferences screen (the webapp's answer to mobile's Settings) ------
    'settings.title': 'Preferences',
    'settings.subtitle': 'Choices that follow you to every device you sign in on.',
    'settings.appearance': 'Appearance',
    'settings.appearanceHint':
      'Dark mode across the whole app. Your choice is remembered per account, so it applies on your phone too.',
    'settings.darkMode': 'Dark Mode',
    'settings.lightMode': 'Light Mode',
    'settings.language': 'Language',
    'settings.languageHint': `Sets the language of the ${PLATFORM.platform_name} interface. What you have typed in — product names, notes, anything else — is never translated.`,
    'settings.account': 'Account',
    'settings.accountHint': 'Your name, phone and other details live on the Profile page.',
    'settings.openProfile': 'Open profile',

    // --- Shared ------------------------------------------------------------
    'common.loading': 'Loading…',
    'common.cancel': 'Cancel',
    'common.close': 'Close',
    'common.save': 'Save',
    'common.edit': 'Edit',
    'common.retry': 'Try again',
    'common.logout': 'Log out',
    'common.logoutAllDevices': 'Log out of all devices',
    'common.logoutAllDevicesConfirm':
      'Log out of all devices? You will need to sign in again everywhere.',
  },

  bengali: {
    // --- Navigation: keyed by menu item / route id --------------------------
    'nav.home': 'ড্যাশবোর্ড',
    'nav.employee-portal': 'ড্যাশবোর্ড',
    'nav.broker-dashboard': 'ড্যাশবোর্ড',
    'nav.revenue': 'আয়',
    'nav.organizations': 'প্রতিষ্ঠানসমূহ',
    'nav.organization-subscriptions': 'প্রতিষ্ঠানের সাবস্ক্রিপশন',
    'nav.organization-setup': 'প্রতিষ্ঠান সেটআপ',
    'nav.my-organizations': 'আমার প্রতিষ্ঠান',
    'nav.owners': 'মালিকগণ',
    'nav.owner': 'মালিক',
    'nav.brokers': 'ব্রোকারগণ',
    'nav.broker': 'ব্রোকার',
    'nav.broker-referrals': 'রেফারেল',
    'nav.broker-profile': 'প্রোফাইল',
    'nav.enterprise': 'এন্টারপ্রাইজ',
    'nav.plans': 'প্ল্যান',
    'nav.plan': 'প্ল্যান',
    'nav.subscriptions': 'সাবস্ক্রিপশন',
    'nav.settings': 'সেটিংস',
    'nav.preferences': 'পছন্দসমূহ',
    'nav.profile': 'প্রোফাইল',
    'nav.users': 'ব্যবহারকারী',
    'nav.managers': 'ম্যানেজার',
    'nav.employees': 'কর্মচারী',
    'nav.roles-permissions': 'ভূমিকা ও অনুমতি',
    'nav.data-review': 'ডেটা পর্যালোচনা',
    'nav.rate-limit': 'রেট লিমিট',
    'nav.api-usage': 'এপিআই ব্যবহার',
    'nav.app-config': 'অ্যাপ কনফিগারেশন',
    'nav.email-config': 'ইমেইল কনফিগারেশন',
    'nav.email-templates': 'ইমেইল টেমপ্লেট',
    'nav.gallery': 'গ্যালারি',
    'nav.trash': 'ট্র্যাশ',
    'nav.about': 'আমাদের সম্পর্কে',
    'nav.contact': 'যোগাযোগ করুন',
    'nav.privacy': 'গোপনীয়তা নীতি',
    'nav.terms': 'পরিষেবার শর্তাবলী',

    // Inventory — this product's own screens (see config/domain-config.tsx).
    'nav.inventory': 'ইনভেন্টরি',
    'nav.products': 'পণ্য',
    'nav.warehouses': 'গুদাম',
    'nav.stock-levels': 'স্টকের পরিমাণ',
    'nav.stock-adjustments': 'স্টক সমন্বয়',
    'nav.categories': 'বিভাগ',
    'nav.customers': 'গ্রাহক',
    'nav.factories': 'কারখানা',
    'nav.brands': 'ব্র্যান্ড',
    'nav.suppliers': 'সরবরাহকারী',
    'nav.orders': 'ক্রয় আদেশ',
    'nav.sales': 'বিক্রয়',
    'nav.invoices': 'চালান',
    'nav.payments': 'পেমেন্ট',

    // --- Modules (the workspace switcher in the top bar) --------------------
    'module.Platform Owner': 'প্ল্যাটফর্ম মালিক',
    'module.Owner': 'মালিক',
    'module.Admin': 'অ্যাডমিন',
    'module.Manager': 'ম্যানেজার',
    'module.Employee': 'কর্মচারী',
    'module.Broker': 'ব্রোকার',

    // --- Top bar -----------------------------------------------------------
    'appbar.toggleSidebar': 'সাইডবার দেখান বা লুকান',
    'appbar.userMenu': 'ব্যবহারকারী মেনু',
    'appbar.language': 'ভাষা',
    'appbar.pageNotFound': 'পৃষ্ঠা পাওয়া যায়নি',

    // --- Preferences screen (the webapp's answer to mobile's Settings) ------
    'settings.title': 'পছন্দসমূহ',
    'settings.subtitle': 'আপনি যে ডিভাইসেই সাইন ইন করুন, এই পছন্দগুলি আপনার সঙ্গে যাবে।',
    'settings.appearance': 'চেহারা',
    'settings.appearanceHint': 'পুরো অ্যাপে ডার্ক মোড। আপনার পছন্দ অ্যাকাউন্টের সঙ্গে সংরক্ষিত থাকে, তাই ফোনেও একই থাকবে।',
    'settings.darkMode': 'ডার্ক মোড',
    'settings.lightMode': 'লাইট মোড',
    'settings.language': 'ভাষা',
    'settings.languageHint': `${PLATFORM.platform_name}-এর ইন্টারফেসের ভাষা ঠিক করে। আপনার নিজের লেখা তথ্য — পণ্যের নাম, নোট বা অন্য কিছু — কখনও অনুবাদ করা হয় না।`,
    'settings.account': 'অ্যাকাউন্ট',
    'settings.accountHint': 'আপনার নাম, ফোন ও অন্যান্য তথ্য প্রোফাইল পৃষ্ঠায় আছে।',
    'settings.openProfile': 'প্রোফাইল দেখুন',

    // --- Shared ------------------------------------------------------------
    'common.loading': 'লোড হচ্ছে…',
    'common.cancel': 'বাতিল',
    'common.close': 'বন্ধ করুন',
    'common.save': 'সংরক্ষণ',
    'common.edit': 'সম্পাদনা',
    'common.retry': 'আবার চেষ্টা করুন',
    'common.logout': 'লগ আউট',
    'common.logoutAllDevices': 'সব ডিভাইস থেকে লগ আউট',
    'common.logoutAllDevicesConfirm':
      'সব ডিভাইস থেকে লগ আউট করবেন? সব জায়গায় আবার সাইন ইন করতে হবে।',
  },
} as const;

export type TranslationKey = keyof typeof translations.english;

// Compile-time proof that the Bengali dictionary covers every English key —
// `Exclude` is `never` only when nothing is missing, and a non-never union
// fails AssertNever's constraint with the missing keys named in the error.
// Cheaper than noticing a gap at runtime, where the key would silently render
// in English.
type AssertNever<T extends never> = T;
export type BengaliCoversEnglish = AssertNever<
  Exclude<TranslationKey, keyof typeof translations.bengali>
>;
